import { useState, useEffect } from 'react';
import { Users, TrendingUp, CheckCircle, AlertTriangle, Plus, Award, Eye } from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { getStaff, getDepartments, getSkills, type Staff, type Skill } from '@/services/staffService';
import { StaffDetailsSheet } from '@/components/StaffDetailsSheet';
import { AddStaffDialog } from '@/components/AddStaffDialog';
import { AddSkillDialog } from '@/components/AddSkillDialog';
import { SkillDetailsSheet } from '@/components/SkillDetailsSheet';

/**
 * CapacitySkillsPage - Organizational capacity and skills management
 * 
 * This page manages:
 * - Employee skills and certifications
 * - Process accountability and ownership
 * - Capacity planning based on skills
 * - Skills library management
 * - Real-time data from API with CRUD operations
 */

// Map API staff data to display format
const mapStaffToEmployee = (staff: Staff) => {
  // Defensive check: ensure skills array exists
  if (!staff || !staff.skills) {
    console.warn('Staff member has no skills array:', staff);
    return null;
  }
  
  // Extract certifications from skills evidence
  const certifications = staff.skills
    .filter(s => s.evidence?.certificateName)
    .map(s => s.evidence!.certificateName!);
  
  // Get skill names
  const skills = staff.skills.map(s => s.skillName);
  
  // Determine seniority from role (simplified mapping)
  let seniority: 'Junior' | 'Mid-level' | 'Senior' | 'Lead' | 'Principal' = 'Mid-level';
  const roleLower = (staff.role || '').toLowerCase();
  if (roleLower.includes('principal')) seniority = 'Principal';
  else if (roleLower.includes('lead') || roleLower.includes('director')) seniority = 'Lead';
  else if (roleLower.includes('senior')) seniority = 'Senior';
  else if (roleLower.includes('junior')) seniority = 'Junior';

  return {
    id: staff.id,
    name: staff.name,
    role: staff.role || 'Unknown',
    department: staff.department || 'Unknown',
    seniority,
    processesOwned: (staff.processes || []).length,
    certifications: [...new Set(certifications)], // Remove duplicates
    skills,
    availability: Math.round(((staff.availableCapacityHours || 0) / (staff.totalCapacityHours || 1)) * 100),
    utilizationRate: staff.utilizationPercent || 0,
  };
};

const getSeniorityColor = (seniority: string) => {
  switch (seniority) {
    case 'Principal':
    case 'Lead':
      return 'text-purple-400 bg-purple-500/20 border-purple-500/30';
    case 'Senior':
      return 'text-primary-400 bg-primary-500/20 border-primary-500/30';
    case 'Mid-level':
      return 'text-warning-400 bg-warning-500/20 border-warning-500/30';
    case 'Junior':
      return 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30';
    default:
      return 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30';
  }
};

export function CapacitySkillsPage() {
  const [activeTab, setActiveTab] = useState<'skills' | 'staff'>('skills');
  const [staffData, setStaffData] = useState<Staff[]>([]);
  const [skillsData, setSkillsData] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All');
  const [departments, setDepartments] = useState<string[]>(['All']);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [isStaffDetailsOpen, setIsStaffDetailsOpen] = useState(false);
  const [isAddStaffOpen, setIsAddStaffOpen] = useState(false);
  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [isSkillDetailsOpen, setIsSkillDetailsOpen] = useState(false);
  const { sectionColor } = useNavigation();

  // Fetch staff and skills data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [staffResponse, skillsResponse, depts] = await Promise.all([
        getStaff(),
        getSkills(),
        getDepartments()
      ]);

      if (staffResponse.success && staffResponse.data) {
        setStaffData(staffResponse.data);
        setDepartments(['All', ...depts]);
      } else {
        setError(staffResponse.error || 'Failed to load data');
      }

      if (skillsResponse.success && skillsResponse.data) {
        setSkillsData(skillsResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (staff: Staff) => {
    setSelectedStaff(staff);
    setIsStaffDetailsOpen(true);
  };

  // Map staff data to employee format for display
  const employees = staffData.map(mapStaffToEmployee).filter((e): e is NonNullable<typeof e> => e !== null);
  
  const filteredEmployees = selectedDepartment === 'All' 
    ? employees 
    : employees.filter(e => e.department === selectedDepartment);

  const totalEmployees = employees.length;
  const totalProcesses = employees.reduce((sum, e) => sum + e.processesOwned, 0);
  const avgUtilization = employees.length > 0 
    ? Math.round(employees.reduce((sum, e) => sum + e.utilizationRate, 0) / employees.length)
    : 0;
  const overutilized = employees.filter(e => e.utilizationRate >= 90).length;

  return (
    <>
      <div className="space-y-6">
        <ComingSoonWatermark
          pageKey="capacity-skills"
          icon={Users}
          title="Capacity & Skills"
          description="Track organizational capacity, employee skills, and certifications"
        />
        
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={cn("text-3xl font-bold", sectionColor)}>Capacity & Skills</h1>
            <p className="text-neutral-400 mt-2">
              Manage organizational capacity, employee skills, certifications, and process accountability
            </p>
          </div>
          <div className="flex gap-2">
            {activeTab === 'skills' ? (
              <button 
                className="btn-primary"
                onClick={() => setIsAddSkillOpen(true)}
              >
                <Award className="w-4 h-4 mr-2" />
                Add Skill
              </button>
            ) : (
              <button 
                className="btn-primary"
                onClick={() => setIsAddStaffOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Employee
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-neutral-800">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('skills')}
              className={cn(
                'pb-3 px-1 border-b-2 font-medium transition-colors',
                activeTab === 'skills'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-neutral-400 hover:text-neutral-300'
              )}
            >
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4" />
                Skills
              </div>
            </button>
            <button
              onClick={() => setActiveTab('staff')}
              className={cn(
                'pb-3 px-1 border-b-2 font-medium transition-colors',
                activeTab === 'staff'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-neutral-400 hover:text-neutral-300'
              )}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Staff
              </div>
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="card p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <p className="text-neutral-400 mt-4">Loading data...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="card p-6 bg-danger-500/10 border-danger-500/30">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-danger-500" />
              <div>
                <p className="text-danger-500 font-medium">Failed to load data</p>
                <p className="text-neutral-400 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Only show when loaded */}
        {!loading && !error && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary-500/20 rounded-lg">
                    <Users className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-400">Total Employees</p>
                    <p className="text-2xl font-bold text-white">{totalEmployees}</p>
                  </div>
                </div>
                <p className="text-xs text-neutral-500">Across all departments</p>
              </div>

              <div className="card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary-500/20 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-400">Processes Owned</p>
                    <p className="text-2xl font-bold text-white">{totalProcesses}</p>
                  </div>
                </div>
                <p className="text-xs text-neutral-500">Total accountability</p>
              </div>

              <div className="card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-warning-500/20 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-warning-500" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-400">Avg Utilization</p>
                    <p className="text-2xl font-bold text-white">{avgUtilization}%</p>
                  </div>
                </div>
                <p className="text-xs text-neutral-500">Team capacity usage</p>
              </div>

              <div className="card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-danger-500/20 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-danger-500" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-400">Overutilized</p>
                    <p className="text-2xl font-bold text-white">{overutilized}</p>
                  </div>
                </div>
                <p className="text-xs text-neutral-500">≥90% utilization</p>
              </div>
            </div>

            {/* Skills Tab Content */}
            {activeTab === 'skills' && (
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Skills Library
                </h2>
                <span className="text-sm text-black/70">{skillsData.length} skills</span>
              </div>
              <div className="p-6">
                {skillsData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-neutral-800">
                          <th className="text-left p-3 text-sm font-medium text-neutral-400">Skill Name</th>
                          <th className="text-left p-3 text-sm font-medium text-neutral-400">Category</th>
                          <th className="text-left p-3 text-sm font-medium text-neutral-400">Staff Count</th>
                          <th className="text-left p-3 text-sm font-medium text-neutral-400">Avg Proficiency</th>
                          <th className="text-right p-3 text-sm font-medium text-neutral-400">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skillsData.map((skill) => (
                          <tr key={skill.id} className="border-b border-neutral-800 hover:bg-neutral-800/30">
                            <td className="p-3 text-white font-medium">{skill.name}</td>
                            <td className="p-3">
                              <span className="badge badge-primary text-xs">{skill.category}</span>
                            </td>
                            <td className="p-3 text-neutral-400">{skill.staffCount || 0} employees</td>
                            <td className="p-3 text-neutral-400">{skill.avgProficiency || 'N/A'}</td>
                            <td className="p-3 text-right">
                              <button
                                onClick={() => {
                                  setSelectedSkill(skill);
                                  setIsSkillDetailsOpen(true);
                                }}
                                className="p-2 hover:bg-neutral-800 rounded-lg transition-colors inline-flex items-center"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4 text-primary-400" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Award className="w-12 h-12 text-neutral-600 mx-auto mb-3" />
                    <p className="text-neutral-400">No skills in library</p>
                    <button 
                      className="btn-primary mt-4"
                      onClick={() => setIsAddSkillOpen(true)}
                    >
                      Add First Skill
                    </button>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Staff Tab Content */}
            {activeTab === 'staff' && (
            <>
            {/* Department Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-neutral-400">Filter by department:</span>
              {departments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setSelectedDepartment(dept)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    selectedDepartment === dept
                      ? 'bg-primary-500 text-black'
                      : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
                  }`}
                >
                  {dept}
                </button>
              ))}
            </div>

            {/* Empty State */}
            {filteredEmployees.length === 0 && (
              <div className="card p-12 text-center">
                <Users className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                <p className="text-neutral-400">No employees found</p>
                <p className="text-neutral-500 text-sm mt-2">
                  {selectedDepartment !== 'All' 
                    ? `No employees in ${selectedDepartment} department` 
                    : 'Add employees to get started'}
                </p>
              </div>
            )}

            {/* Employees Table */}
            {filteredEmployees.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold">Employee Skills & Capacity</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-800">
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Name</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Role</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Department</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Seniority</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Processes</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Certifications</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Utilization</th>
                        <th className="text-right p-4 text-sm font-medium text-neutral-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployees.map((employee) => {
                        const staff = staffData.find(s => s.id === employee.id);
                        return (
                          <tr key={employee.id} className="border-b border-neutral-800 hover:bg-neutral-800/30 transition-colors">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-primary-500 to-cyan-500">
                                  <span className="text-sm font-bold text-black">
                                    {employee.name.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                                <span className="text-white font-medium">{employee.name}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-neutral-300">{employee.role}</span>
                            </td>
                            <td className="p-4">
                              <span className="text-neutral-400 text-sm">{employee.department}</span>
                            </td>
                            <td className="p-4">
                              <span className={`badge ${getSeniorityColor(employee.seniority)}`}>
                                {employee.seniority}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-primary-400" />
                                <span className="text-white font-medium">{employee.processesOwned}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex flex-wrap gap-1">
                                {employee.certifications.length > 0 ? (
                                  <>
                                    {employee.certifications.slice(0, 2).map((cert, idx) => (
                                      <span key={idx} className="badge badge-primary text-xs">
                                        {cert}
                                      </span>
                                    ))}
                                    {employee.certifications.length > 2 && (
                                      <span className="badge badge-primary text-xs">
                                        +{employee.certifications.length - 2}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-neutral-500 text-xs">None</span>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="space-y-1">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-neutral-400">Utilization</span>
                                  <span className={`font-medium ${
                                    employee.utilizationRate >= 90 ? 'text-danger-500' :
                                    employee.utilizationRate >= 75 ? 'text-warning-500' :
                                    'text-success-500'
                                  }`}>
                                    {employee.utilizationRate}%
                                  </span>
                                </div>
                                <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full ${
                                      employee.utilizationRate >= 90 ? 'bg-danger-500' :
                                      employee.utilizationRate >= 75 ? 'bg-warning-500' :
                                      'bg-success-500'
                                    }`}
                                    style={{ width: `${employee.utilizationRate}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 text-right">
                              <button 
                                className="btn-outline text-xs py-1 px-3"
                                onClick={() => staff && handleViewDetails(staff)}
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <StaffDetailsSheet
        staff={selectedStaff}
        isOpen={isStaffDetailsOpen}
        onClose={() => setIsStaffDetailsOpen(false)}
        onUpdate={fetchData}
      />

      <SkillDetailsSheet
        skill={selectedSkill}
        isOpen={isSkillDetailsOpen}
        onClose={() => {
          setIsSkillDetailsOpen(false);
          setSelectedSkill(null);
        }}
        onUpdate={fetchData}
      />

      <AddStaffDialog
        isOpen={isAddStaffOpen}
        onClose={() => setIsAddStaffOpen(false)}
        onSuccess={fetchData}
      />

      <AddSkillDialog
        isOpen={isAddSkillOpen}
        onClose={() => setIsAddSkillOpen(false)}
        onSuccess={fetchData}
      />
    </>
  );
}